import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { HttpClient, HttpParams, HttpContext } from '@angular/common/http';
import { HttpContextConfig } from '../models/http-context-config';
import { CONFIG } from '../interceptors/loader.interceptor';
import { environment } from 'src/environments/environment';

const baseURL = `${environment.api}/users/`;

export interface Filestorage {
  id: string;
  path: string;
}

export interface DisabledUser {
  id: string;
  filestorage: Filestorage;
  first_name: string;
  last_name: string;
  hiring_date: string;
  resignation_date: string;
  role?: Array<{
    id?: string;
    name?: string;
    display_name?: string;
  }>;
}

export interface Roles {
  name: string;
  display_name: string;
}

export interface Assignments {
  id: number;
  alias: string;
}

export interface DisabledUsersResponse {
  result: DisabledUser[];
  total: number;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  findElem: any;

  constructor(private http: HttpClient) {}

  selectUserReview(params?: any): Observable<any> {
    return of({ result: [] });
  }

  selectUserReviewFeed(params?: any): Observable<any> {
    return of({ result: [] });
  }

  getUsers(params?: any, includeMe: boolean = false): Observable<any> {
    const config = new HttpContextConfig();
    config.noSpinner = true;
    const httpParams = { ...params, includeMe: includeMe.toString() };
    return this.http.get<any>(baseURL + 'index', {
      params: httpParams,
      context: new HttpContext().set(CONFIG, config)
    });
  }

  getUsersAdmin(params?: any): Observable<any> {
    const config = new HttpContextConfig();
    config.noSpinner = true;

    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach(key => {
        const value = params[key];

        if (Array.isArray(value)) {
          const isArrayParam =
            key === 'filter_role' ||
            key === 'filter_assignment' ||
            key === 'filter_expertise' ||
            key === 'filter_visibility';

          const paramKey = isArrayParam ? `${key}[]` : key;

          if (key === 'filter_visibility') {
            value.forEach(item => {
              const numValue = item === 'true' || item === '1' ? 1 : 0;
              httpParams = httpParams.append(paramKey, numValue.toString());
            });
          } else {
            value.forEach(item => {
              httpParams = httpParams.append(paramKey, item);
            });
          }
        } else if (value !== null && value !== undefined && value !== '') {
          httpParams = httpParams.set(key, value);
        }
      });
    }
    return this.http.get<any>(baseURL + 'enabledUsers', {
      params: httpParams,
      context: new HttpContext().set(CONFIG, config)
    });
  }

  editUser(id: string, user: any): Observable<any> {
    return this.http.put<any>(baseURL + `putUser/${id}`, user);
  }

  getUserContract(id: string): Observable<any> {
    return this.http.get<any>(baseURL + `${id}/contract`);
  }

  saveUserContract(id: string, payload: any): Observable<any> {
    return this.http.post<any>(baseURL + `${id}/contract`, payload);
  }

  changeIsVisibleInAllocation(
    id: string,
    isVisibleInAllocation: boolean
  ): Observable<any> {
    return of({ result: { id, visible_in_allocation: isVisibleInAllocation } });
  }

  /**
   * Recupera utenti disabilitati
   * @param params parametri opzionali (filterSearch, filterSortField, filterSortDirection)
   */
  getDisabledUsers(params?: {
    filterSearch?: string;
    filterSortField?: string;
    filterSortDirection?: string;
    skip?: number;
    take?: number;
  }): Observable<DisabledUsersResponse> {
    let httpParams = new HttpParams();

    if (params) {
      if (params.filterSearch) {
        httpParams = httpParams.set('filter_search', params.filterSearch);
      }

      if (params.filterSortField && params.filterSortDirection) {
        httpParams = httpParams
          .set('filter_sort_field', params.filterSortField)
          .set('filter_sort_direction', params.filterSortDirection);
      }

      if (params.skip != null) {
        httpParams = httpParams.set('skip', params.skip.toString());
      }

      if (params.take != null) {
        httpParams = httpParams.set('take', params.take.toString());
      }
    }

    return this.http.get<DisabledUsersResponse>(baseURL + 'disabledUsers', {
      params: httpParams
    });
  }

  getAllDisabledUsers(): Observable<DisabledUsersResponse> {
    return this.http.get<DisabledUsersResponse>(baseURL + 'disabledUsers');
  }

  enableUser(id: string): Observable<void> {
    return of(undefined);
  }

  getCountUser(params?: any): Observable<any> {
    const config = new HttpContextConfig();
    config.noSpinner = true;
    return this.http.get<any>(baseURL + 'countUser', {
      params,
      context: new HttpContext().set(CONFIG, config)
    });
  }

  getRoleFilterOptions(): Observable<Roles[]> {
    return this.http.get<any>(baseURL + 'rolesFilter').pipe(
      map(res => {
        const roles: any[] = Array.isArray(res?.result) ? res.result : [];
        return roles.map(item => {
          // Usa 'name' come id (es. "admin", "project_manager"), che è stabile
          return {
            name: item.name, // Nuovo campo stabile per controlli
            display_name: item.display_name
          } as Roles & { name: string }; // Estendi l'interfaccia
        });
      })
    );
  }

  getAssignmentFilterOptions(): Observable<Assignments[]> {
    return this.http.get<any>(baseURL + 'assignmentRolesFilter').pipe(
      map(res => {
        const assignments: any[] = Array.isArray(res?.result) ? res.result : [];

        return assignments.map(item => {
          return {
            id: item.id,
            alias: item.alias
          } as Assignments;
        });
      })
    );
  }

  rehabilitateUser(id: string): Observable<void> {
    const config = new HttpContextConfig();
    config.noSpinner = false;
    const body = { status: true, resignation_date: null };
    const httpOptions = {
      headers: { 'Content-Type': 'application/json' },
      context: new HttpContext().set(CONFIG, config)
    };
    return this.http.patch<void>(
      `${baseURL}toggleAccount/${id}`,
      body,
      httpOptions
    );
  }

  disableUser(
    id: string,
    status: boolean,
    resignation_date: string
  ): Observable<any> {
    const body = { status, resignation_date };
    const httpOptions = {
      headers: { 'Content-Type': 'application/json' }
    };
    return this.http.patch<any>(
      `${baseURL}toggleAccount/${id}`,
      body,
      httpOptions
    );
  }

  toggleAccount(id: string, resignation_date: string): Observable<any> {
    const body = { id, resignation_date };
    const httpOptions = {
      headers: { 'Content-Type': 'application/json' }
    };
    return this.http.post<any>(`${baseURL}disableUser`, body, httpOptions);
  }

  checkEmail(params: any): Observable<any> {
    const config = new HttpContextConfig();
    config.noSpinner = true;
    return this.http.get<any>(baseURL + 'checkEmail', {
      params,
      context: new HttpContext().set(CONFIG, config)
    });
  }

  checkAzureMail(params: any): Observable<any> {
    const config = new HttpContextConfig();
    config.noSpinner = true;
    return this.http.get<any>(baseURL + 'checkAzureMail', {
      params,
      context: new HttpContext().set(CONFIG, config)
    });
  }

  getArgumentsProf(id: string): Observable<any> {
    return of({ result: null });
  }
  /**
   * Aggiorna la configurazione oraria (ore giornaliere previste e straordinario)
   * di un utente specifico.
   *
   * @param id - ID dell'utente
   * @param payload - Dati contenenti expected_minutes e max_overtime_minutes
   */
  updateCapacitySettings(id: string, payload: any) {
    return of({ result: payload });
  }
}
